import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material'
import {
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { subDays, format } from 'date-fns'
import integrationService from '../../services/integrationService'
import type { ServiceType } from '../../types/integration'

/** Usage tab of the Integration Monitor: API calls, success rate and top errors per integration. */
const IntegrationUsagePanel: React.FC = () => {
  // Default to last 30 days
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 30))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [serviceTypeFilter, setServiceTypeFilter] = useState<ServiceType | ''>('')
  const [isExporting, setIsExporting] = useState(false)

  // Fetch analytics data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['integration-analytics', serviceTypeFilter, startDate, endDate],
    queryFn: () => {
      const params: any = {}
      if (serviceTypeFilter) params.service_type = serviceTypeFilter
      if (startDate) params.start_date = format(startDate, 'yyyy-MM-dd')
      if (endDate) params.end_date = format(endDate, 'yyyy-MM-dd')
      return integrationService.getAnalytics(params)
    },
    staleTime: 60000 // Consider data stale after 1 minute
  })

  // Export to CSV handler
  const handleExportCSV = async () => {
    try {
      setIsExporting(true)
      const params: any = {}
      if (serviceTypeFilter) params.service_type = serviceTypeFilter
      if (startDate) params.start_date = format(startDate, 'yyyy-MM-dd')
      if (endDate) params.end_date = format(endDate, 'yyyy-MM-dd')

      const blob = await integrationService.exportAnalytics(params, 'csv')
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `integration-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to export analytics:', err)
    } finally {
      setIsExporting(false)
    }
  }

  // Format number with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString()
  }

  // Format percentage
  const formatPercentage = (num: number): string => {
    return `${num.toFixed(2)}%`
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          Failed to load analytics data. Please try again.
        </Alert>
      </Box>
    )
  }

  const analytics = data?.analytics
  const integrations = data?.integrations || []
  const topErrors = data?.top_errors || []

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Toolbar */}
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
          <Button
            variant="contained"
            startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleExportCSV}
            disabled={isExporting}
          >
            Export to CSV
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Service Type</InputLabel>
                  <Select
                    value={serviceTypeFilter}
                    label="Service Type"
                    onChange={(e) => setServiceTypeFilter(e.target.value as ServiceType | '')}
                  >
                    <MenuItem value="">All Services</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="whatsapp">WhatsApp</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                    <MenuItem value="payment">Payment</MenuItem>
                    <MenuItem value="logistics">Logistics</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => refetch()}
                >
                  Apply Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">
                    Total API Calls
                  </Typography>
                </Box>
                <Typography variant="h3" component="div">
                  {formatNumber(analytics?.total_requests || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">
                    Success Rate
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" color="success.main">
                  {formatPercentage(analytics?.success_rate || 0)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatNumber(analytics?.successful_requests || 0)} successful
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ErrorIcon color="error" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">
                    Failed Requests
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" color="error.main">
                  {formatNumber(analytics?.failed_requests || 0)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatPercentage(100 - (analytics?.success_rate || 0))} failure rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <SpeedIcon color="info" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">
                    Avg Response Time
                  </Typography>
                </Box>
                <Typography variant="h3" component="div">
                  {Math.round(analytics?.average_response_time || 0)}
                  <Typography variant="caption" component="span" ml={1}>
                    ms
                  </Typography>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Integration Details Table */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Integration Usage Details
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Service Type</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell align="right">Total Calls</TableCell>
                    <TableCell align="right">Successful</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Error Rate</TableCell>
                    <TableCell align="right">Avg Response (ms)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {integrations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary" py={2}>
                          No data available for the selected period
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    integrations.map((integration, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip
                            label={integration.service_type}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{integration.provider_name}</TableCell>
                        <TableCell align="right">{formatNumber(integration.total_calls)}</TableCell>
                        <TableCell align="right">
                          <Typography color="success.main">
                            {formatNumber(integration.successful_calls)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="error.main">
                            {formatNumber(integration.failed_calls)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={formatPercentage(integration.error_rate)}
                            size="small"
                            color={integration.error_rate > 10 ? 'error' : integration.error_rate > 5 ? 'warning' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {Math.round(integration.average_response_time)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Top Errors */}
        {topErrors.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Error Messages
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Error Message</TableCell>
                      <TableCell align="right">Occurrences</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topErrors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" color="error">
                            {error.error_message || 'Unknown error'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={formatNumber(error.count)} size="small" color="error" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default IntegrationUsagePanel
